import parse from 'html-react-parser';
import isEmpty from 'lodash/isEmpty';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import EmptyArticle from 'src/components/empty';
import Layout from 'src/components/layouts';
import { APP_URL, IS_NO_ROBOT_INDEX, WEB_NAME } from 'src/constants';
import { IMAGE_DEFAULT, WEB_URL } from 'src/constants/layout';
import { GET } from 'src/services';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { escapeChar, useDesktop } from 'src/utils/helper';
import useUser from 'src/utils/useUser';

const GalleryPreview = dynamic(
  () => import('src/components/gallery/gallery-preview')
);

const PageGalleriesPreview = (props) => {
  const _data = useUser();
  const router = useRouter();
  const _length = props?.article?.data?.length;
  const _index = props?.article?.data?.findIndex(
    (item) => item.id === props?.id
  );
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  const { article, meta, dataAdsHeader } = props;
  // console.log('PageGalleriesPreview', props);
  if (isEmpty(article)) return <EmptyArticle sessionSevId={sessionSevId} />;
  const heading = `${props?.article?.title} รูปภาพที่ ${_index + 1}/${_length}`;
  const articleTitleSeo = escapeChar(`รวมอัลบั้มรูป : ${heading ?? 'ไม่พบข้อมูล'}`);
  const articleDescriptionSeo = escapeChar(meta?.description);
  const baseUrlCategory = `${APP_URL}${router.asPath}`;

  return (
    <>
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={`รวมอัลบั้มรูป : ${heading ?? 'ไม่พบข้อมูล'}`}
        description={meta?.description}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: `รวมอัลบั้มรูป : ${heading ?? 'ไม่พบข้อมูล'}`,
          description: meta?.description,
          url: baseUrlCategory,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: '1920',
              height: '1080',
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: meta?.keyword
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={`รวมอัลบั้มรูป : ${heading ?? 'ไม่พบข้อมูล'}`} />
        <meta property='twitter:description' content={meta?.description} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* --------------------------------- Truehit -------------------------------- */}
        <script type='text/javascript' dangerouslySetInnerHTML={{
          __html: `
            __th_page="galleries";
          `
        }} />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "${WEB_NAME}",
              "url": "${WEB_URL.WEB}",
              "description": "${articleDescriptionSeo}",
              "sameAs": [
                "${WEB_URL.FACEBOOK}",
                "${WEB_URL.TWITTER}",
                "${WEB_URL.YOUTUBE}",
                "${WEB_URL.LINE}"
              ],
              "potentialAction": {
                "@type": "SearchAction",
                "target": "${WEB_URL.WEB}search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "http://schema.org",
                "@type": "WebPage",
                "name": "${articleTitleSeo}",
                "description": "${articleDescriptionSeo}",
                "publisher": {
                  "@type": "ProfilePage",
                  "name": "${WEB_NAME}"
                }
              }
            `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script type='application/ld+json' dangerouslySetInnerHTML={{
          __html: ` 
              { 
                "@context": "http://schema.org", 
                "@type": "BreadcrumbList", 
                "itemListElement": [ 
                  { 
                    "@type": "ListItem", 
                    "position": 1, 
                    "name": "หน้าแรก", 
                    "item": "${APP_URL}" 
                  }, 
                  { 
                    "@type": "ListItem", 
                    "position": 2, 
                    "name": "${articleTitleSeo}", 
                    "item": "${baseUrlCategory}" 
                  } 
                ] 
              } 
            `
        }} />
        {!useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}

        </Head>
      <Layout sessionSevId={sessionSevId}>
      <h1 className='head-seo'>{`รวมอัลบั้มรูป : ${heading ?? 'ไม่พบข้อมูล'}`}</h1>

        <GalleryPreview {...props} />
        </Layout>
    </>
  );
};

export async function getServerSideProps ({ req, res, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    article: {},
    related: [],
    lastNews: [],
    solrRelated: [],
    affiliate: [],
    adsNative: null,
    ads: [],
    sessionSevId: null,
    id: null,
    group: null
  };
  try {
    const id = query.group ? query.group : query.id;
    const resData = await GET(`/api/v1.0/gallery/contents/${id}`, false, true);
    const adsHeaders = await GET('/api/headerAds');
    if (!isEmpty(adsHeaders)) {
      initProps.dataAdsHeader = adsHeaders;
    }
    if (!isEmpty(resData?.data)) {
      initProps.article = (resData?.data);
      initProps.meta = (resData?.meta);

      initProps.related = resData?.related ? resData?.related.slice(0, 5) : [];
      initProps.lastNews = resData?.last_news ? resData?.last_news.slice(0, 5) : [];
      initProps.solrRelated = resData?.solrRelated ? resData?.solrRelated.slice(0, 5) : [];
      initProps.affiliate = resData?.affiliate || [];
      initProps.ads = resData?.ads || [];
      initProps.adsNative = resData?.adsNative || null;
      initProps.id = query?.id;
      initProps.group = query?.group;
    }
  } catch (err) {
    console.log('==========> LOTTERY ERROR', err?.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;

    res.statusCode = initProps.statusCode;
    res.end();
  }

  return {
    props: initProps
  };
}

export default PageGalleriesPreview;
